import React, { useState, ChangeEvent, FormEvent, memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  message: string;
}

const Contactus: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  }, []);

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const form = e.currentTarget;
      const data = new FormData(form);
      
      const response = await fetch(form.action, {
        method: form.method,
        body: data,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setIsSubmitted(false), 6000);
      } else {
        throw new Error('Form submission encountered an infrastructure network issue.');
      }
    } catch (err) {
      setError('An error occurred while submitting the form. Please connect directly via WhatsApp or email.');
      console.error('Form submission error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const isFormValid = formData.name.trim() && formData.email.trim() && formData.message.trim();

  return (
    <div className="relative min-h-screen bg-gray-950 flex justify-center items-center py-16 px-4 sm:px-6 lg:px-8 overflow-hidden font-sans antialiased">
      
      {/* 
        FIXED ASSET PATH: Uses a clean absolute path mapping to your public directory.
        This forces Webpack to leave the string alone during the compilation step.
      */}
      <div 
        className="absolute inset-0 z-0 bg-cover bg-center opacity-25 scale-105 pointer-events-none"
        style={{ backgroundImage: "url('/images/contactus.webp')" }}
      ></div>
      
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-gray-950 via-gray-950/90 to-gray-950 pointer-events-none"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-6xl bg-gray-900/60 backdrop-blur-xl border border-gray-800/80 rounded-3xl p-6 sm:p-10 lg:p-12 shadow-2xl shadow-black/50">
        
        <header className="text-center mb-12 sm:mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-green-500 bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20">
            Get In Touch
          </span>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white mt-4 mb-3">
            Connect with Wardha Mushroom
          </h1>
          <p className="text-sm sm:text-base text-gray-400 max-w-2xl mx-auto font-medium">
            Whether ordering bulk fresh produce, dried stocks, or checking program seat availabilities—drop a direct log below.
          </p>
        </header>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          
          <div className="lg:col-span-2 space-y-4">
            
            <div className="p-5 bg-gray-950/40 border border-gray-800/60 rounded-2xl flex items-start gap-4 hover:border-gray-700/60 transition-colors group">
              <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 group-hover:bg-green-500 group-hover:text-white transition-all">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Direct Business Lines</h4>
                <p className="text-base font-bold text-white mt-1">+91 7263071957</p>
                <p className="text-xs text-gray-500 mt-0.5">Available Mon-Sat for trade inquiries</p>
              </div>
            </div>

            <div className="p-5 bg-gray-950/40 border border-gray-800/60 rounded-2xl flex items-start gap-4 hover:border-gray-700/60 transition-colors group">
              <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-all">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Corporate Desk</h4>
                <p className="text-base font-bold text-white mt-1 break-all">wardhamushroom@gmail.com</p>
                <p className="text-xs text-gray-500 mt-0.5">Expect replies within 12 business hours</p>
              </div>
            </div>

            <div className="p-5 bg-gray-950/40 border border-gray-800/60 rounded-2xl flex items-start gap-4 hover:border-gray-700/60 transition-colors group">
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 group-hover:bg-amber-500 group-hover:text-white transition-all">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Production Facility</h4>
                <p className="text-sm font-bold text-white mt-1 leading-relaxed">
                  Wardha Mushroom Sole Proprietorship,<br />
                  Wardha, Maharashtra, India
                </p>
                <p className="text-xs text-gray-500 mt-1">Industrial Climate Controlled Agro Unit</p>
              </div>
            </div>

          </div>

          <div className="lg:col-span-3 bg-gray-950/30 border border-gray-800/40 p-6 sm:p-8 rounded-2xl relative">
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="py-12 text-center flex flex-col items-center justify-center"
                  role="alert"
                >
                  <div className="p-4 bg-green-500/10 text-green-400 rounded-full border border-green-500/20 mb-4 animate-bounce">
                    <CheckCircle2 className="w-12 h-12" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Message Dispatched</h3>
                  <p className="text-sm text-gray-400 max-w-sm">
                    Thank you! Your inquiry has been securely sent. Our desk will trace back to your email shortly.
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <h3 className="text-lg font-bold text-white mb-6">Send An Instant Log</h3>
                  
                  {error && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-xs flex items-center gap-2" role="alert">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <form 
                    onSubmit={handleSubmit} 
                    className="space-y-4"
                    action="https://formsubmit.co/wardhamushroom@gmail.com" 
                    method="POST"
                  >
                    <input type="hidden" name="_subject" value="New corporate contact form routing via WardhaMushroom.com" />
                    <input type="hidden" name="_captcha" value="false" />

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Full Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Gaurav Thakare"
                          className="w-full px-4 py-3 bg-gray-900/80 border border-gray-800 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Email Routing Address</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="name@company.com"
                          className="w-full px-4 py-3 bg-gray-900/80 border border-gray-800 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Message Body</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Detail your request here (e.g. Commercial spawn distribution setups or bulk dried oyster purchase inquiries...)"
                        rows={4}
                        className="w-full px-4 py-3 bg-gray-900/80 border border-gray-800 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all resize-none"
                        required
                      ></textarea>
                    </div>

                    <div className="pt-2">
                      <button 
                        type="submit" 
                        disabled={isLoading || !isFormValid}
                        className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all duration-200 ${
                          !isFormValid || isLoading
                            ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700/40'
                            : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-950/40 hover:brightness-110 active:scale-98'
                        }`}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Dispatching Log...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Transmit Message
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </div>
    </div>
  );
};

export default memo(Contactus);